/**
 * CardStack - Card deck view mode for collected content
 *
 * Displays stack items as flip-through cards with:
 * - Arrow navigation + keyboard shortcuts
 * - Draggable dot indicators for reordering
 * - Touch swipe on mobile
 * - Card counter
 * - Empty state with instructions
 * - Inline editing (controlled by toolbar Edit button)
 * - List view toggle (B3)
 *
 * @module @humanizer/studio/components/workspace/CardStack
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { StackCard } from './StackCard';
import { StackListView } from './StackListView';
import { OutlinePanel, type DraftMeta, type DraftProvenance } from './OutlinePanel';
import { useBufferSync } from '../../contexts/BufferSyncContext';
import { useToast } from '../../contexts/ToastContext';
import { createLogger } from '../../utils/debug';
import type { CuratorSearchResult } from '../../types/electron';

const log = createLogger('CardStack');

interface CullCandidate {
  index: number;
  score: number;
  reasoning: string;
  recommended: 'keep' | 'cull';
  constraintScore?: number | null;
  managerVoice?: number | null;
}

interface CardStackProps {
  /** Whether the active card is in edit mode (controlled by toolbar) */
  isEditing?: boolean;
  /** Callback when editing state should change (save/cancel) */
  onEditingChange?: (editing: boolean) => void;
  /** B10: Callback to show draft alongside source cards in split view */
  onViewWithSources?: (draftText: string, sourceCards: Array<{ title: string; excerpt: string }>, provenances?: DraftProvenance[]) => void;
  /** Callback when draft is created (for chapter linking in MainWorkspace) */
  onDraftCreated?: (text: string, sourceCards?: Array<{ title: string; excerpt: string }>, provenances?: DraftProvenance[], draftMeta?: DraftMeta) => void;
  /** Callback when outline modal opens (ensures book+chapter exists before generation) */
  onOutlineOpen?: () => Promise<void>;
}

export function CardStack({ isEditing = false, onEditingChange, onViewWithSources, onDraftCreated, onOutlineOpen }: CardStackProps) {
  const {
    stackItems,
    activeStackIndex,
    setActiveStackIndex,
    addToStack,
    removeFromStack,
    insertAtIndex,
    updateStackItem,
    updateDividerTitle,
    addDividerToStack,
    reorderStack,
    clearStack,
    importText,
  } = useBufferSync();
  const { addToast } = useToast();

  // Outline panel state
  const [showOutline, setShowOutline] = useState(false);

  // B10/C4: Source cards + provenance for "View with Sources" after draft generation
  const [draftSourceCards, setDraftSourceCards] = useState<Array<{ title: string; excerpt: string }> | null>(null);
  const [draftProvenances, setDraftProvenances] = useState<DraftProvenance[] | null>(null);

  // List view mode (persisted to electron-store)
  const [listMode, setListMode] = useState(false);
  useEffect(() => {
    if (window.electronAPI?.store?.get) {
      window.electronAPI.store.get('stackListMode').then((val: unknown) => {
        if (typeof val === 'boolean') setListMode(val);
      }).catch(() => {});
    }
  }, []);
  const toggleListMode = useCallback(() => {
    setListMode(prev => {
      const next = !prev;
      if (window.electronAPI?.store?.set) {
        window.electronAPI.store.set('stackListMode', next).catch(() => {});
      }
      return next;
    });
  }, []);

  // Section input popover state (replaces prompt())
  const [showSectionInput, setShowSectionInput] = useState(false);
  const [sectionTitle, setSectionTitle] = useState('');
  const sectionInputRef = useRef<HTMLInputElement>(null);

  // Phase 3: Search/fill/cull state
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CuratorSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFilling, setIsFilling] = useState(false);
  const [fillTarget, setFillTarget] = useState(10);
  const [fillProgress, setFillProgress] = useState('');
  const [starredIndices, setStarredIndices] = useState<Set<number>>(new Set());
  const [isCulling, setIsCulling] = useState(false);
  const [cullResults, setCullResults] = useState<CullCandidate[] | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const fillAbortRef = useRef(false);

  // Animation direction
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const animKey = useRef(0);

  // Undo state for removed cards
  const [undoItem, setUndoItem] = useState<{
    item: (typeof stackItems)[0];
    index: number;
  } | null>(null);

  // Drag state for dot indicators
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Exit edit mode when navigating to a different card
  const prevActiveIndex = useRef(activeStackIndex);
  useEffect(() => {
    if (prevActiveIndex.current !== activeStackIndex && isEditing) {
      onEditingChange?.(false);
    }
    prevActiveIndex.current = activeStackIndex;
  }, [activeStackIndex, isEditing, onEditingChange]);

  const goTo = useCallback((index: number, direction: 'left' | 'right') => {
    if (index < 0 || index >= stackItems.length) return;
    setSlideDirection(direction);
    animKey.current++;
    setActiveStackIndex(index);
  }, [stackItems.length, setActiveStackIndex]);

  const goPrev = useCallback(() => {
    if (activeStackIndex > 0) goTo(activeStackIndex - 1, 'left');
  }, [activeStackIndex, goTo]);

  const goNext = useCallback(() => {
    if (activeStackIndex < stackItems.length - 1) goTo(activeStackIndex + 1, 'right');
  }, [activeStackIndex, stackItems.length, goTo]);

  const goFirst = useCallback(() => {
    if (activeStackIndex > 0) goTo(0, 'left');
  }, [activeStackIndex, goTo]);

  const goLast = useCallback(() => {
    if (activeStackIndex < stackItems.length - 1) goTo(stackItems.length - 1, 'right');
  }, [activeStackIndex, stackItems.length, goTo]);

  const handleRemove = useCallback((index: number) => {
    const removed = stackItems[index];
    if (!removed) return;
    setUndoItem({ item: removed, index });
    removeFromStack(index);
    addToast('Card removed', 'info', {
      duration: 4000,
      action: {
        label: 'Undo',
        onClick: () => {
          insertAtIndex(removed, index);
          setUndoItem(null);
        },
      },
    });
  }, [stackItems, removeFromStack, insertAtIndex, addToast]);

  const handleCardSave = useCallback((text: string) => {
    updateStackItem(activeStackIndex, text);
    onEditingChange?.(false);
  }, [activeStackIndex, updateStackItem, onEditingChange]);

  const handleCardCancel = useCallback(() => {
    onEditingChange?.(false);
  }, [onEditingChange]);

  const handleCardEdit = useCallback((index: number) => {
    setActiveStackIndex(index);
    onEditingChange?.(true);
  }, [setActiveStackIndex, onEditingChange]);

  const handleMoveLeft = useCallback(() => {
    if (activeStackIndex > 0) {
      reorderStack(activeStackIndex, activeStackIndex - 1);
      setActiveStackIndex(activeStackIndex - 1);
    }
  }, [activeStackIndex, reorderStack, setActiveStackIndex]);

  const handleMoveRight = useCallback(() => {
    if (activeStackIndex < stackItems.length - 1) {
      reorderStack(activeStackIndex, activeStackIndex + 1);
      setActiveStackIndex(activeStackIndex + 1);
    }
  }, [activeStackIndex, stackItems.length, reorderStack, setActiveStackIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (isInput) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goPrev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goNext();
          break;
        case 'Home':
          e.preventDefault();
          goFirst();
          break;
        case 'End':
          e.preventDefault();
          goLast();
          break;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          if (stackItems.length > 0) {
            handleRemove(activeStackIndex);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goPrev, goNext, goFirst, goLast, handleRemove, activeStackIndex, stackItems.length]);

  // Touch swipe
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    touchStartRef.current = null;

    // Only trigger if horizontal swipe is dominant and > 50px
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) goNext();
      else goPrev();
    }
  }, [goNext, goPrev]);

  // Drag-to-reorder handlers for dot indicators
  const handleDotDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  }, []);

  const handleDotDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDotDrop = useCallback((e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== toIndex) {
      reorderStack(dragIndex, toIndex);
      // Follow the dragged card
      if (activeStackIndex === dragIndex) {
        setActiveStackIndex(toIndex);
      }
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, activeStackIndex, reorderStack, setActiveStackIndex]);

  const handleDotDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
  }, []);

  // Section creation with inline input (replaces prompt())
  const handleAddSection = useCallback(() => {
    setShowSectionInput(true);
    setSectionTitle('');
    setTimeout(() => sectionInputRef.current?.focus(), 0);
  }, []);

  const handleSectionSubmit = useCallback(() => {
    const trimmed = sectionTitle.trim();
    if (trimmed) {
      addDividerToStack(trimmed).catch(err => {
        log.warn('Failed to add section divider:', err);
        addToast('Failed to add section', 'warning');
      });
    }
    setShowSectionInput(false);
    setSectionTitle('');
  }, [sectionTitle, addDividerToStack, addToast]);

  const handleSectionKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSectionSubmit();
    } else if (e.key === 'Escape') {
      setShowSectionInput(false);
      setSectionTitle('');
    }
  }, [handleSectionSubmit]);

  // ─────────────────────────────────────────────────────────────────────────
  // PHASE 3: SEARCH / FILL / STAR / CULL
  // ─────────────────────────────────────────────────────────────────────────

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || !window.electronCurator) return;
    setIsSearching(true);
    setSearchResults([]);
    try {
      const { anchorPassage } = await window.electronCurator.synthesize(searchQuery.trim());
      const results = await window.electronCurator.searchWithAnchor(anchorPassage, { limit: 20 });
      // Deduplicate against items already in stack (by nodeId)
      const existingNodeIds = new Set(
        stackItems.map(si => si.contentItem.metadata.source?.nodeId).filter(Boolean)
      );
      const filtered = results.filter((r: CuratorSearchResult) => !existingNodeIds.has(r.nodeId));
      setSearchResults(filtered);
    } catch (err) {
      log.error('Search failed:', err);
      addToast('Search failed', 'warning');
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, stackItems, addToast]);

  const handleAddSearchResult = useCallback(async (result: CuratorSearchResult) => {
    const item = {
      id: result.nodeId,
      type: 'archive-node' as const,
      text: result.text,
      metadata: {
        source: {
          type: result.sourceType,
          path: [result.title ?? 'Search result'],
          nodeId: result.nodeId,
          threadRootId: result.threadRootId,
          threadTitle: result.title,
        },
        authorRole: (result.authorRole as 'user' | 'assistant' | 'system' | 'tool') ?? undefined,
        timestamp: result.sourceCreatedAt ? new Date(result.sourceCreatedAt).getTime() : undefined,
        wordCount: result.text.split(/\s+/).filter(Boolean).length,
      },
    };
    await addToStack(item);
    // Remove from results list
    setSearchResults(prev => prev.filter(r => r.nodeId !== result.nodeId));
    addToast('Added to stack', 'info');
  }, [addToStack, addToast]);

  const handleFill = useCallback(async () => {
    if (!window.electronCurator) return;
    setIsFilling(true);
    fillAbortRef.current = false;

    const thresholds = [0.7, 0.55, 0.4, 0.25, 0.1, 0.0];
    const seen = new Set<string>(
      stackItems.map(si => si.contentItem.metadata.source?.nodeId).filter(Boolean) as string[]
    );
    const collected: CuratorSearchResult[] = [];

    // Use starred cards as anchors, or the search query
    let anchors: string[] = [];
    if (starredIndices.size > 0) {
      anchors = Array.from(starredIndices)
        .map(i => stackItems[i]?.contentItem.text)
        .filter(Boolean) as string[];
    } else if (searchQuery.trim()) {
      try {
        const { anchorPassage } = await window.electronCurator.synthesize(searchQuery.trim());
        anchors = [anchorPassage];
      } catch {
        addToast('Could not generate search anchor', 'warning');
        setIsFilling(false);
        return;
      }
    } else {
      addToast('Enter a search query or star cards first', 'info');
      setIsFilling(false);
      return;
    }

    const target = fillTarget;

    for (const threshold of thresholds) {
      if (fillAbortRef.current) break;
      if (stackItems.length + collected.length >= target) break;

      setFillProgress(`Searching (${(threshold * 100).toFixed(0)}%)... ${collected.length} found`);

      for (const anchor of anchors) {
        if (fillAbortRef.current) break;
        if (stackItems.length + collected.length >= target) break;

        try {
          const results = await window.electronCurator.searchWithAnchor(anchor, { limit: 50 });
          for (const r of results) {
            if (seen.has(r.nodeId)) continue;
            if (r.similarity < threshold) continue;
            seen.add(r.nodeId);
            collected.push(r);
            if (stackItems.length + collected.length >= target) break;
          }
        } catch (err) {
          log.warn('Fill search pass failed:', err);
        }
      }
    }

    setFillProgress(`Adding ${collected.length} cards...`);

    // Add collected results to stack
    for (const result of collected) {
      if (fillAbortRef.current) break;
      await handleAddSearchResult(result);
    }

    setFillProgress('');
    setIsFilling(false);
    if (collected.length > 0) {
      addToast(`Added ${collected.length} cards`, 'success');
    } else {
      addToast('No new results found', 'info');
    }
  }, [stackItems, starredIndices, searchQuery, fillTarget, addToast, handleAddSearchResult]);

  const handleToggleStar = useCallback((index: number) => {
    setStarredIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const handleCull = useCallback(async () => {
    if (!window.electronBuffer?.cullStack) return;
    if (stackItems.length < 3) {
      addToast('Need at least 3 cards to cull', 'info');
      return;
    }

    setIsCulling(true);
    setCullResults(null);
    try {
      const cardTexts = stackItems
        .filter(si => !si.isDivider)
        .map(si => si.contentItem.text);
      const { candidates } = await window.electronBuffer.cullStack({ cardTexts });
      setCullResults(candidates);
      if (candidates.length === 0) {
        addToast('All cards look relevant', 'success');
      }
    } catch (err) {
      log.error('Cull failed:', err);
      addToast('Cull analysis failed', 'warning');
    } finally {
      setIsCulling(false);
    }
  }, [stackItems, addToast]);

  const handleApplyCull = useCallback(() => {
    if (!cullResults) return;
    // Remove culled items in reverse order to preserve indices
    const toRemove = cullResults
      .filter(c => c.recommended === 'cull')
      .map(c => c.index)
      .sort((a, b) => b - a);
    for (const idx of toRemove) {
      removeFromStack(idx);
    }
    addToast(`Removed ${toRemove.length} cards`, 'info');
    setCullResults(null);
  }, [cullResults, removeFromStack, addToast]);

  const handleDraftCreated = useCallback(async (draftText: string, sourceCards?: Array<{ title: string; excerpt: string }>, provenances?: DraftProvenance[], draftMeta?: DraftMeta) => {
    setShowOutline(false);
    try {
      if (window.electronBuffer) {
        // B10: Auto-checkpoint active buffer before saving draft
        const activeCard = stackItems[activeStackIndex];
        if (activeCard?.bufferId && window.electronBuffer.createVersion) {
          try {
            await window.electronBuffer.createVersion(activeCard.bufferId, 'Before draft generation', undefined, 'auto');
          } catch (err) {
            log.warn('Auto-checkpoint failed:', err);
            addToast('Auto-checkpoint failed', 'warning');
          }
        }

        const saved = await window.electronBuffer.createFromText(draftText, {
          format: 'markdown',
          initialState: 'staged',
          metadata: {
            generatedDraft: true,
            sourceCardCount: stackItems.length,
            ...(draftMeta?.arcType && { arcType: draftMeta.arcType }),
            ...(draftMeta?.outlineSections && { outlineSections: draftMeta.outlineSections }),
          },
        });
        const wordCount = draftText.split(/\s+/).filter(Boolean).length;
        addToast(`Draft created (${wordCount} words)`, 'success');
        // Import the draft text into the workspace buffer
        await importText(draftText, 'Generated Draft', { bufferId: saved.id, generatedDraft: true });

        // B10/C4: Store source cards + provenance for "View with Sources" reference
        if (sourceCards && sourceCards.length > 0) {
          setDraftSourceCards(sourceCards);
          setDraftProvenances(provenances ?? null);
        }
      } else {
        await importText(draftText, 'Generated Draft', { generatedDraft: true });
        addToast(`Draft created (${draftText.split(/\s+/).filter(Boolean).length} words)`, 'success');
      }
      // Notify parent for chapter linking
      onDraftCreated?.(draftText, sourceCards, provenances, draftMeta);
    } catch (err) {
      log.error('Failed to save draft:', err);
      addToast('Draft generated but could not be saved', 'warning');
    }
  }, [stackItems, activeStackIndex, importText, addToast, onDraftCreated]);

  // List view: navigate to card
  const handleListNavigate = useCallback((index: number) => {
    setActiveStackIndex(index);
    setListMode(false);
  }, [setActiveStackIndex]);

  // Empty state
  if (stackItems.length === 0) {
    return (
      <div className="card-stack">
        <div className="card-stack__empty">
          <div className="card-stack__empty-title">No cards in stack</div>
          <div className="card-stack__empty-hint">
            Click content in the archive panel to add cards.
            Use arrow keys to flip through, Delete to remove.
          </div>
        </div>
      </div>
    );
  }

  const currentItem = stackItems[activeStackIndex];
  if (!currentItem) return null;

  const slideClass = slideDirection === 'left'
    ? 'card-stack__card-wrapper--entering-left'
    : slideDirection === 'right'
    ? 'card-stack__card-wrapper--entering-right'
    : '';

  // Limit dots to 20 to avoid clutter
  const showDots = stackItems.length <= 20;

  return (
    <div
      className="card-stack"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {listMode ? (
        <StackListView
          items={stackItems}
          activeIndex={activeStackIndex}
          onNavigate={handleListNavigate}
          onRemove={handleRemove}
          onReorder={reorderStack}
        />
      ) : (
        <div className="card-stack__viewport">
          {/* Prev arrow */}
          <button
            className="card-stack__nav card-stack__nav--prev"
            onClick={goPrev}
            disabled={activeStackIndex <= 0}
            aria-label="Previous card"
          >
            &#8249;
          </button>

          {/* Card */}
          <div className={`card-stack__card-wrapper ${slideClass}`} key={animKey.current}>
            <StackCard
              item={currentItem}
              index={activeStackIndex}
              isEditing={isEditing}
              isStarred={starredIndices.has(activeStackIndex)}
              onStar={() => handleToggleStar(activeStackIndex)}
              onEdit={() => handleCardEdit(activeStackIndex)}
              onRemove={() => handleRemove(activeStackIndex)}
              onSave={handleCardSave}
              onCancel={handleCardCancel}
              onDividerTitleChange={updateDividerTitle}
              onMoveLeft={handleMoveLeft}
              onMoveRight={handleMoveRight}
              canMoveLeft={activeStackIndex > 0}
              canMoveRight={activeStackIndex < stackItems.length - 1}
            />
          </div>

          {/* Next arrow */}
          <button
            className="card-stack__nav card-stack__nav--next"
            onClick={goNext}
            disabled={activeStackIndex >= stackItems.length - 1}
            aria-label="Next card"
          >
            &#8250;
          </button>
        </div>
      )}

      {/* Phase 3: Search panel (between viewport and footer) */}
      {showSearch && (
        <div className="card-stack__search-panel">
          <div className="card-stack__search-header">
            <input
              ref={searchInputRef}
              className="card-stack__search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              placeholder="Search archive..."
              disabled={isSearching}
            />
            <button
              className="card-stack__action-btn card-stack__action-btn--primary"
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
            <div className="card-stack__fill-controls">
              <label className="card-stack__fill-label">
                Fill to
                <input
                  className="card-stack__fill-input"
                  type="number"
                  min={stackItems.length + 1}
                  max={50}
                  value={fillTarget}
                  onChange={(e) => setFillTarget(Number(e.target.value) || 10)}
                />
              </label>
              <button
                className="card-stack__action-btn"
                onClick={isFilling ? () => { fillAbortRef.current = true; } : handleFill}
                disabled={!isFilling && !searchQuery.trim() && starredIndices.size === 0}
              >
                {isFilling ? 'Stop' : 'Fill'}
              </button>
            </div>
            <button
              className="card-stack__action-btn"
              onClick={handleCull}
              disabled={isCulling || stackItems.length < 3}
              title="Analyze cards for relevance"
            >
              {isCulling ? 'Analyzing...' : 'Cull'}
            </button>
          </div>

          {fillProgress && (
            <div className="card-stack__fill-progress">{fillProgress}</div>
          )}

          {searchResults.length > 0 && (
            <div className="card-stack__search-results">
              {searchResults.map((result) => (
                <div key={result.nodeId} className="card-stack__search-result">
                  <div className="card-stack__search-result-text">
                    <span className="card-stack__search-result-title">
                      {result.title ?? result.sourceType}
                    </span>
                    <span className="card-stack__search-result-excerpt">
                      {result.text.slice(0, 120)}
                      {result.text.length > 120 ? '...' : ''}
                    </span>
                    <span className="card-stack__search-result-meta">
                      {(result.similarity * 100).toFixed(0)}% match
                      {result.sourceType ? ` \u00b7 ${result.sourceType}` : ''}
                    </span>
                  </div>
                  <button
                    className="card-stack__action-btn"
                    onClick={() => handleAddSearchResult(result)}
                  >
                    Add
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Cull results */}
          {cullResults && cullResults.length > 0 && (
            <div className="card-stack__cull-results" aria-live="polite">
              <div className="card-stack__cull-header">
                <span className="card-stack__cull-title">Cull recommendations</span>
                <button
                  className="card-stack__action-btn card-stack__action-btn--primary"
                  onClick={handleApplyCull}
                >
                  Remove {cullResults.filter(c => c.recommended === 'cull').length} culled
                </button>
                <button
                  className="card-stack__action-btn"
                  onClick={() => setCullResults(null)}
                >
                  Dismiss
                </button>
              </div>
              {cullResults.map((c) => (
                <div
                  key={c.index}
                  className={`card-stack__cull-item card-stack__cull-item--${c.recommended}`}
                >
                  <span className="card-stack__cull-badge">
                    {c.recommended === 'cull' ? 'CULL' : 'KEEP'}
                  </span>
                  <span className="card-stack__cull-card-title">
                    Card {c.index + 1}: {stackItems[c.index]?.summary ?? ''}
                  </span>
                  <span className="card-stack__cull-reasoning">{c.reasoning}</span>
                  <span className="card-stack__cull-score">{(c.score * 100).toFixed(0)}%</span>
                  {c.constraintScore != null && (
                    <span
                      className="card-stack__cull-constraint"
                      title={`SIC: ${(c.constraintScore * 100).toFixed(0)}%${c.managerVoice != null ? ` · Manager voice: ${(c.managerVoice * 100).toFixed(0)}%` : ''}`}
                    >
                      <span
                        className="card-stack__cull-signal-dot"
                        style={{ background: c.constraintScore > 0.5 ? 'var(--color-success, #22c55e)' : c.managerVoice != null && c.managerVoice > 0.5 ? 'var(--color-warning, #f59e0b)' : 'var(--studio-text-tertiary)' }}
                      />
                      SIC {(c.constraintScore * 100).toFixed(0)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="card-stack__footer">
        <span className="card-stack__counter">
          {activeStackIndex + 1} of {stackItems.length}
          {starredIndices.size > 0 && ` \u00b7 ${starredIndices.size}\u2605`}
        </span>

        {showDots && !listMode && (
          <div className="card-stack__indicators">
            {stackItems.map((item, i) => (
              <button
                key={item.bufferId}
                className={`card-stack__dot${i === activeStackIndex ? ' card-stack__dot--active' : ''}${dragOverIndex === i ? ' card-stack__dot--drag-over' : ''}${item.isDivider ? ' card-stack__dot--divider' : ''}${starredIndices.has(i) ? ' card-stack__dot--starred' : ''}`}
                onClick={() => goTo(i, i < activeStackIndex ? 'left' : 'right')}
                draggable
                onDragStart={(e) => handleDotDragStart(e, i)}
                onDragOver={(e) => handleDotDragOver(e, i)}
                onDrop={(e) => handleDotDrop(e, i)}
                onDragEnd={handleDotDragEnd}
                onKeyDown={(e) => {
                  if (e.ctrlKey || e.metaKey) {
                    if (e.key === 'ArrowLeft' && i > 0) {
                      e.preventDefault();
                      reorderStack(i, i - 1);
                      if (activeStackIndex === i) setActiveStackIndex(i - 1);
                    } else if (e.key === 'ArrowRight' && i < stackItems.length - 1) {
                      e.preventDefault();
                      reorderStack(i, i + 1);
                      if (activeStackIndex === i) setActiveStackIndex(i + 1);
                    }
                  }
                }}
                aria-label={`${item.isDivider ? 'Section' : 'Card'} ${i + 1}${i === activeStackIndex ? ' (current)' : ''}${starredIndices.has(i) ? ' (starred)' : ''}. Ctrl+Arrow to reorder`}
              />
            ))}
          </div>
        )}

        <div className="card-stack__actions">
          {/* Search toggle */}
          <button
            className={`card-stack__action-btn${showSearch ? ' card-stack__action-btn--active' : ''}`}
            onClick={() => {
              setShowSearch(prev => {
                if (!prev) setTimeout(() => searchInputRef.current?.focus(), 0);
                return !prev;
              });
            }}
            title="Search & fill"
          >
            {showSearch ? 'Close Search' : 'Search'}
          </button>

          {/* View toggle */}
          <button
            className="card-stack__action-btn"
            onClick={toggleListMode}
            title={listMode ? 'Card view' : 'List view'}
          >
            {listMode ? '\u2630' : '\u2261'}
          </button>

          {/* Section input */}
          {showSectionInput ? (
            <div className="card-stack__section-input-wrap">
              <input
                ref={sectionInputRef}
                className="card-stack__section-input"
                value={sectionTitle}
                onChange={(e) => setSectionTitle(e.target.value)}
                onKeyDown={handleSectionKeyDown}
                onBlur={handleSectionSubmit}
                placeholder="Section title..."
                aria-label="Section title"
              />
            </div>
          ) : (
            <button
              className="card-stack__action-btn"
              onClick={handleAddSection}
            >
              + Section
            </button>
          )}

          {draftSourceCards && onViewWithSources && (
            <button
              className="card-stack__action-btn card-stack__action-btn--primary"
              onClick={() => {
                // Use the current buffer text as draft
                const activeCard = stackItems[activeStackIndex];
                if (activeCard) {
                  onViewWithSources(activeCard.contentItem.text, draftSourceCards, draftProvenances ?? undefined);
                }
              }}
              title="View draft alongside source cards"
            >
              View with Sources
            </button>
          )}
          {stackItems.length >= 2 && (
            <button
              className="card-stack__action-btn card-stack__action-btn--primary"
              onClick={() => { onOutlineOpen?.(); setShowOutline(true); }}
            >
              Generate Draft
            </button>
          )}
          {stackItems.length > 0 && (
            <button
              className="card-stack__action-btn"
              onClick={clearStack}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {showOutline && (
        <OutlinePanel
          stackItems={stackItems}
          onClose={() => setShowOutline(false)}
          onDraftCreated={handleDraftCreated}
        />
      )}
    </div>
  );
}
